import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const StaffMaster = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Store fetched departments for the dropdown
  const [departmentsList, setDepartmentsList] = useState([]);

  const initialFormState = {
    staff_cd: "",
    staff_name: "",
    role: "",
    department: "",
    shift_type: "",
    contact_no: "",
    email: "",
    status: "Active",
    _isExisting: false
  };

  const [formData, setFormData] = useState(initialFormState);

  // --- NEW: GENERATE FORMATTED STAFF ID ---
  const getNewStaffId = () => {
    const date = new Date();
    return `STF-${date.getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
  };

  // --- INITIAL DATA LOAD (Departments) ---
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/departments?employee_id=${user?.id}`);
        const data = await res.json();
        setDepartmentsList(data.filter(d => d.status === "Active"));
      } catch (err) {
        console.error("Failed to load departments", err);
      }
    };
    fetchDepartments();
  }, []);

  // --- 1. SEARCH LOGIC ---
  const searchStaff = async (text) => {
    setSearchText(text);
    if (text.length < 2) {
      setStaffList([]);
      setShowDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/staff/search?q=${text}&employee_id=${user?.id}`);
      const data = await res.json();
      setStaffList(data);
      setShowDropdown(true);
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  const selectStaff = (staff) => {
    setFormData({
      ...staff,
      _isExisting: true
    });
    setSearchText(`${staff.staff_name} | ${staff.staff_cd}`);
    setShowDropdown(false);

    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // --- 2. FORM ACTIONS ---
  const clearForm = () => {
    setFormData({
      ...initialFormState,
      staff_cd: getNewStaffId()
    });
    setSearchText("");
    setIsDisabled(false);
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    if (screenAction === "add") clearForm();
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // --- 3. SAVE LOGIC ---
  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!formData.staff_name || !formData.role || !formData.department) {
        return alert("Staff Name, Role, and Department are required!");
    }

    const payload = {
      ...formData,
      created_by: user?.id
    };
    
    delete payload._isExisting;

    const method = formData._isExisting ? "PUT" : "POST";
    const url = method === "POST" 
      ? "https://hmsapi.emedhatechnologies.com/master/api/api/staff" 
      : `https://hmsapi.emedhatechnologies.com/master/api/api/staff/${formData.staff_cd}`;

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        alert("Staff Profile Saved Successfully!");
        if (method === "POST") clearForm();
      } else {
        alert("Error saving staff profile");
      }
    } catch (err) {
      alert("Network error while saving.");
    }
  };

  // --- 4. PRINT FORM ---
  const printForm = () => {
    const newWindow = window.open("", "", "width=900,height=700");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Staff Profile - ${formData.staff_name}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #e67e22; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #2c3e50; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #7f8c8d; }
            .report-title { text-align: right; }
            .report-title h2 { margin: 0; color: #e67e22; font-size: 18px; }
            .section-header { background: #fdf2e9; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #e67e22; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; }
            .data-item { font-size: 13px; display: flex; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; width: 160px; color: #555; }
            .value { flex: 1; color: #000; font-weight: 500; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>Staff Master Record</p>
            </div>
            <div class="report-title">
              <h2>STAFF PROFILE</h2>
              <p>Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Employment Information</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Staff Code:</span> <span class="value">${formData.staff_cd}</span></div>
            <div class="data-item"><span class="label">Status:</span> <span class="value">${formData.status}</span></div>
            <div class="data-item"><span class="label">Staff Name:</span> <span class="value">${formData.staff_name}</span></div>
            <div class="data-item"><span class="label">Role/Designation:</span> <span class="value">${formData.role}</span></div>
            <div class="data-item"><span class="label">Department:</span> <span class="value">${formData.department}</span></div>
            <div class="data-item"><span class="label">Shift Type:</span> <span class="value">${formData.shift_type || 'N/A'}</span></div>
          </div>

          <div class="section-header">Contact Information</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Contact Number:</span> <span class="value">${formData.contact_no || 'N/A'}</span></div>
            <div class="data-item"><span class="label">Email Address:</span> <span class="value">${formData.email || 'N/A'}</span></div>
          </div>

          <div class="footer">
            System Generated Report - Emedha HMS
          </div>
        </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();
  };

  return (
    <div className="registration-container fade-in">
      
      {/* SEARCH BAR */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Staff</label>
          <input
            type="text"
            value={searchText}
            placeholder="Search by ID, Name, or Role..."
            onChange={(e) => searchStaff(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (staffList.length === 1) selectStaff(staffList[0]);
              }
            }}
            autoFocus
          />
          {showDropdown && staffList.length > 0 && (
            <div className="patient-dropdown">
              {staffList.map((staff) => (
                <div key={staff.staff_cd} className="patient-item" onClick={() => selectStaff(staff)}>
                  {staff.staff_name} | ID: {staff.staff_cd} | {staff.role}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        
        {/* IDENTIFICATION & ROLE */}
        <div className="form-section-header">
          <h3>Employment Information</h3>
        </div>
        <div className="form-grid">
          <div className="input-box read-only">
            <label>Staff Code</label>
            <input type="text" value={formData.staff_cd} readOnly placeholder="Auto Generated" />
          </div>
          <div className="input-box">
            <label>Staff Name <span style={{color:'red'}}>*</span></label>
            <input type="text" name="staff_name" value={formData.staff_name} onChange={handleChange} disabled={isDisabled} placeholder="e.g., Sita Kumari" />
          </div>
          
          <div className="input-box">
            <label>Role / Designation <span style={{color:'red'}}>*</span></label>
            <select name="role" value={formData.role} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select Role</option>
              <option value="Nurse">Nurse</option>
              <option value="Lab Technician">Lab Technician</option>
              <option value="Pharmacist">Pharmacist</option>
              <option value="Receptionist">Receptionist / Front Desk</option>
              <option value="Ward Boy / Aya">Ward Boy / Aya</option>
              <option value="Housekeeping">Housekeeping</option>
              <option value="Accountant">Accountant / Cashier</option>
              <option value="HR / Admin">HR / Admin</option>
            </select>
          </div>

          <div className="input-box">
            <label>Department <span style={{color:'red'}}>*</span></label>
            <select name="department" value={formData.department} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select Department</option>
              {departmentsList.map(dept => (
                <option key={dept.dept_cd} value={dept.dept_name}>
                  {dept.dept_name}
                </option>
              ))}
            </select>
          </div>

          <div className="input-box">
            <label>Shift Type</label>
            <select name="shift_type" value={formData.shift_type} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select Shift</option>
              <option value="Morning">Morning (8 AM - 4 PM)</option>
              <option value="Evening">Evening (4 PM - 12 AM)</option>
              <option value="Night">Night (12 AM - 8 AM)</option>
              <option value="General">General (10 AM - 6 PM)</option>
            </select>
          </div>

          <div className="input-box">
            <label>Status</label>
            <select name="status" value={formData.status} onChange={handleChange} disabled={isDisabled}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="On Leave">On Leave</option>
            </select>
          </div>
        </div>

        {/* DETAILS & CONTACT */}
        <div className="form-section-header">
          <h3>Contact Information</h3>
        </div>
        <div className="form-grid">
          <div className="input-box">
            <label>Contact Number</label>
            <input type="text" name="contact_no" value={formData.contact_no} onChange={handleChange} disabled={isDisabled} placeholder="Phone No." />
          </div>
          <div className="input-box">
            <label>Email Address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={isDisabled} placeholder="staff@hospital.com" />
          </div>
        </div>

      </form>
    </div>
  );
};

export default StaffMaster;